import {AtUri} from '@atproto/api'
import {useQuery} from '@tanstack/react-query'

import {PUBLIC_BSKY_SERVICE} from '#/lib/constants'
import {
  type FlashBookDraftRef,
  flashBookDraftRefFromView,
  type FlashbookDraftView,
} from '#/lib/flashbookDrafts'
import {createQueryKey} from '#/state/queries/util'
import {useAgent} from '#/state/session'
import {type FlashBookPage} from '#/types/flashbook'

const flashbookByUriQueryKeyRoot = 'flashbookByUri'
const flashbookByPostQueryKeyRoot = 'flashbookByPost'
const actorFlashbooksQueryKeyRoot = 'actorFlashbooks'
const actorFlashbookDraftsQueryKeyRoot = 'actorFlashbookDrafts'

export const createFlashbookByUriQueryKey = (flashbookUri: string) =>
  createQueryKey(flashbookByUriQueryKeyRoot, {flashbookUri})

/**
 * A fetched book record, as every reader of one wants it.
 *
 * One shape for both ways of asking — by URI, and by the post it belongs to —
 * because they are the same record reached from two directions, and a card that
 * had to know which direction it came from would drift the moment a field is
 * added.
 */
export type FlashbookRecordView = {
  uri: string
  cid: string
  title?: string
  description?: string
  themeId?: string
  behaviorSettings: Record<string, unknown>
  cover?: {
    kind: 'image' | 'audio' | 'video'
    imageUri?: string
    audioUri?: string
    videoUri?: string
    /** Which minute the cover film opens on (see `flashbookCoverTrim`). */
    videoStartMs?: number
    videoThumbnailUri?: string
  }
  pages: FlashBookPage[]
  pageCount?: number
  exerciseCount?: number
  subject?: string
  level?: string
  status?: 'draft' | 'published'
}

/** Read a book record's fields, whatever it was reached through. */
function flashbookViewOf(
  uri: string,
  cid: string,
  value: Record<string, unknown>,
): FlashbookRecordView {
  return {
    uri,
    cid,
    title: value.title as string | undefined,
    description: value.description as string | undefined,
    themeId: value.themeId as string | undefined,
    behaviorSettings:
      (value.behaviorSettings as Record<string, unknown> | undefined) ?? {},
    cover: value.cover as FlashbookRecordView['cover'],
    pages: (value.pages as FlashBookPage[] | undefined) ?? [],
    pageCount: value.pageCount as number | undefined,
    exerciseCount: value.exerciseCount as number | undefined,
    subject: value.subject as string | undefined,
    level: value.level as string | undefined,
    status: value.status as 'draft' | 'published' | undefined,
  }
}

/**
 * Fetches an `app.bsky.feed.flashbook` record directly by its AT URI.
 *
 * Used by FlashBookPlay, previews, and the feed card — when the record URI is
 * known, which is exactly what the post's back-ref gives. It reads the PDS
 * rather than the AppView's `getFlashbook` for the reason the rest of the app
 * reads records: the post names a URI and this is what lives at it, so the card
 * keeps working on a deployment whose AppView has not been taught about books.
 */
export function useFlashbookByUriQuery(flashbookUri: string | undefined) {
  const agent = useAgent()

  return useQuery({
    queryKey: createFlashbookByUriQueryKey(flashbookUri ?? ''),
    enabled: !!flashbookUri,
    queryFn: async () => {
      const atUri = new AtUri(flashbookUri!)
      const res = await agent.com.atproto.repo.getRecord({
        repo: atUri.hostname,
        collection: atUri.collection,
        rkey: atUri.rkey,
      })
      return flashbookViewOf(res.data.uri, res.data.cid ?? '', res.data.value)
    },
    staleTime: 1000 * 60 * 5, // 5 min
  })
}

export const createFlashbookByPostQueryKey = (postUri: string) =>
  createQueryKey(flashbookByPostQueryKeyRoot, {postUri})

/**
 * Finds the book a post belongs to, by looking through its author's records.
 *
 * The slow way round, and the one that exists because publishing a book is two
 * writes: the post first, then the same post again with the book's back-ref on
 * it. A creator whose second write failed has a perfectly good published book
 * and a post that points at nothing, and "the book I just published does not
 * appear in the feed" is not an acceptable answer to that.
 *
 * Authors rarely have many books, so this pages the author's books and stops at
 * the first whose `post` points back. It only runs when the post itself says
 * nothing, so a healthy feed never makes this request.
 */
export function useFlashbookByPostQuery(postUri: string, authorDid: string) {
  const agent = useAgent()

  return useQuery({
    queryKey: createFlashbookByPostQueryKey(postUri),
    enabled: !!postUri && !!authorDid,
    queryFn: async (): Promise<FlashbookRecordView | null> => {
      let cursor: string | undefined
      while (true) {
        const res = await agent.com.atproto.repo.listRecords({
          repo: authorDid,
          collection: 'app.bsky.feed.flashbook',
          limit: 50,
          cursor,
        })
        const match = res.data.records.find(record => {
          const value = record.value as Record<string, unknown> | undefined
          const post = value?.post as {uri?: unknown} | undefined
          return !!post && post.uri === postUri
        })
        if (match) {
          const value = match.value as Record<string, unknown>
          // A draft is not on this post — it has no post — so a record found
          // through the post's own URI is necessarily published. The status
          // still travels with it, because the card shows nothing for a draft.
          return flashbookViewOf(match.uri, match.cid ?? '', value)
        }
        cursor = res.data.cursor
        if (!cursor) return null
      }
    },
    staleTime: 1000 * 60 * 5, // 5 min
  })
}

/**
 * One of an author's own book records, as a picker needs to list it.
 *
 * Not a `FlashbookRecordView`: that is what a *reader* needs, reached by an
 * address, and it has no idea whose work it is or whether it was ever published.
 * This is what a record says about itself when a person is choosing between their
 * books — the title to read, the year and subject to filter by, how long it is and
 * how much of it is work, its cover, and the addresses of the two records it is.
 *
 * `postUri` is absent for a **draft**: a book with no post is unfinished, private
 * work, and a picker of things to put in a room has nothing to say about it. The
 * records are still returned, so that reading them is one question with one
 * answer and each caller leaves out what it must (see `visibility` below).
 */
export type ActorFlashbookRecord = {
  /** AT-URI of the `app.bsky.feed.flashbook` record. */
  uri: string
  cid: string
  title: string
  description: string
  themeId?: string
  /** The grade code the publisher chose (`Level_A5`), not a name. */
  level?: string
  subject?: string
  /** Pages in the book, as the publisher counted them. */
  pageCount?: number
  /** Blocks in the book that are exercises, as the publisher counted them. */
  exerciseCount?: number
  /** The cover, as the record stores it. A still, or a film and its own frame. */
  cover?: FlashbookRecordView['cover']
  status?: string
  /** The post this book was published as. Absent for a draft. */
  postUri?: string
  /**
   * Who the book was written for (`classroom` when it belongs to one).
   *
   * A book made for a classroom *is* published as a post, so its `postUri` is set
   * and it is indistinguishable from a public book by its addresses alone. This
   * is the field that tells the two apart, and the one a caller reads to leave a
   * room's own book out of anything that shares books around.
   */
  visibility?: string
  createdAt: string
}

export const createActorFlashbooksQueryKey = (actor: string) =>
  createQueryKey(actorFlashbooksQueryKeyRoot, {actor})

/**
 * Every book record an author has written, drafts included.
 *
 * Read from the repo rather than from the author's post feed, for the reason the
 * capsule picker reads records: a *post* carries a back-reference to its book and
 * none of the book's own fields, so a picker built from posts can show a title and
 * nothing else and has no way to filter by year or subject at all. One
 * `listRecords` answers every question a chooser has at once.
 *
 * The same shape as `useActorCapsulesQuery` on purpose — the two pickers are one
 * mechanism over two vocabularies, and a book picker that answered a different
 * question from the capsule picker beside it would be a second thing to learn.
 */
export function useActorFlashbooksQuery(actor: string | undefined) {
  const agent = useAgent()

  return useQuery({
    queryKey: createActorFlashbooksQueryKey(actor ?? ''),
    enabled: !!actor,
    queryFn: async (): Promise<ActorFlashbookRecord[]> => {
      const out: ActorFlashbookRecord[] = []
      let cursor: string | undefined
      while (true) {
        const res = await agent.com.atproto.repo.listRecords({
          repo: actor!,
          collection: 'app.bsky.feed.flashbook',
          limit: 100,
          cursor,
        })
        for (const record of res.data.records) {
          const value = (record.value ?? {}) as Record<string, unknown>
          const pages = Array.isArray(value.pages) ? value.pages : undefined
          out.push({
            uri: record.uri,
            cid: record.cid ?? '',
            title: (value.title as string | undefined) ?? '',
            description: (value.description as string | undefined) ?? '',
            themeId: (value.themeId as string | undefined) ?? undefined,
            level: (value.level as string | undefined) ?? undefined,
            subject: (value.subject as string | undefined) ?? undefined,
            pageCount:
              typeof value.pageCount === 'number'
                ? value.pageCount
                : pages?.length,
            exerciseCount:
              typeof value.exerciseCount === 'number'
                ? value.exerciseCount
                : undefined,
            cover: value.cover as FlashbookRecordView['cover'],
            status: (value.status as string | undefined) ?? undefined,
            postUri:
              ((value.post as {uri?: string} | undefined)?.uri as
                | string
                | undefined) ?? undefined,
            visibility: (value.visibility as string | undefined) ?? undefined,
            createdAt: (value.createdAt as string | undefined) ?? '',
          })
        }
        if (!res.data.cursor || res.data.records.length < 100) break
        cursor = res.data.cursor
      }
      return out
    },
    staleTime: 1000 * 60 * 5, // 5 min
  })
}

export const createActorFlashbookDraftsQueryKey = (actor: string) =>
  createQueryKey(actorFlashbookDraftsQueryKeyRoot, {actor})

/**
 * The creator's own unpublished books, from the server.
 *
 * Drafts are private records, so they can never be reached from a feed and the
 * only way back to them is to ask for them by author. Asking the server rather
 * than reading a device-local list is the whole point: half-finished work has
 * to be there when the creator opens the studio on their laptop, not only on
 * the phone that saved it.
 *
 * Two details that are easy to get wrong and fail silently:
 *
 *  • **The host is the AppView.** `getActorFlashbooks` is implemented in the
 *    `bsky` service, not the PDS, because deciding which drafts a viewer may
 *    see is a reading concern. Asking the PDS would 404, the query would look
 *    like "no drafts", and the creator's work would appear to have vanished.
 *  • **It is called through `fetch`, not the agent.** The endpoint is a Taderok
 *    extension that is not among the custom lexicons the frontend registers
 *    (`state/session/lexicons`), so `agent.app.bsky.feed.getActorFlashbooks`
 *    would throw for an unknown method. Same convention as `getWallet`.
 */
export function useActorFlashbookDraftsQuery(actor: string | undefined) {
  const agent = useAgent()

  return useQuery({
    queryKey: createActorFlashbookDraftsQueryKey(actor ?? ''),
    enabled: !!actor,
    queryFn: async (): Promise<FlashBookDraftRef[]> => {
      const accessJwt = agent.session?.accessJwt
      if (!accessJwt) throw new Error('Not authenticated')

      const params = new URLSearchParams({
        actor: actor!,
        status: 'draft',
        limit: '50',
      })
      const res = await fetch(
        `${PUBLIC_BSKY_SERVICE}/xrpc/app.bsky.feed.getActorFlashbooks?${params}`,
        {headers: {Authorization: `Bearer ${accessJwt}`}},
      )
      if (!res.ok) {
        // A deployment that does not serve this method yet answers 501, and one
        // that has never heard of it answers 404. Neither is worth a retry storm
        // or a console error: report "no drafts from the server", which the merge
        // treats as "keep what this device has" — exactly the behaviour the
        // studio shipped before the server list existed. The feature switches
        // itself on when the backend that implements the handler is deployed.
        if (res.status === 501 || res.status === 404) return []
        const err = (await res.json().catch(() => ({}))) as {message?: string}
        throw new Error(err.message ?? `HTTP ${res.status}`)
      }
      const data = (await res.json()) as {flashbooks?: FlashbookDraftView[]}
      return (data.flashbooks ?? [])
        .map(flashBookDraftRefFromView)
        .filter((draft): draft is FlashBookDraftRef => draft !== null)
    },
    // Brief: drafts change whenever the creator saves, and a stale row is
    // worse than a small refetch.
    staleTime: 1000 * 30,
  })
}
